import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Order, Party, OrderHistoryComponent, OrderUIConfiguration, OrderMetaDataRepositoryService, eOrderType, Utilities, UserGroupAccessRepositoryService, eOrderStatusType, TimelineEvent, ConsortiumService, SlideDownAnimation, OrderRepositoryService, TimelineSummary, ConfigurationState, AttestationTemplateRepositoryService, UboTreeNode, eIdentityVerificationResult, eIdentityVerificationSource } from 'reg-hub-common';
import { Observable, of, Subject, takeUntil } from 'rxjs';
import { OrdersHubService } from '../../services/hubs/orders-hub/orders-hub.service';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { EnumLabelService } from 'reg-hub-common';

@Component({
  selector: 'app-order-details',
  templateUrl: './order-details.component.html',
  styleUrls: ['./order-details.component.css'],
  animations: [SlideDownAnimation],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class OrderDetailsComponent implements OnInit, OnDestroy {
  private readonly enumLabels = inject(EnumLabelService);
  private readonly transloco = inject(TranslocoService);
  @Input() order!: Order;
  @Input() uiConfiguration!: OrderUIConfiguration;
  @Input() loading: boolean = true;
  @Input() uboTreeNode?: UboTreeNode;
  orderTypeName: string = "";
  attestationTemplateName: string = "";
  utilities = Utilities;

  debtors!: Observable<Party[]>;
  securedParties!: Observable<Party[]>;
  dealers!: Observable<Party[]>;
  registeringAgents!: Observable<Party[]>;
  assignors!: Observable<Party[]>;
  assignees!: Observable<Party[]>;
  businessParties!: Observable<Party[]>;
  attestor!: Party | undefined;

  summary: TimelineSummary[] = [];
  consortiumHintSearchJurisdictions: string[] = [];

  debtorsTitle: string = 'orders.debtors';
  securedPartiesTitle: string = 'common.labels.securedParties';
  dealersTitle: string = 'common.labels.dealers';
  registeringAgentsTitle: string = 'common.labels.registeringAgents';
  partiesTitle: string = 'common.labels.parties';
  showEstate: boolean = false;
  requestingUser: string = "";
  requestingUserGroup: string = "";
  _hasMultipleUserGroups = false;
  _showJSONParsedResults = false;

  private onDestroy$ = new Subject<void>();

  constructor(private dialog: MatDialog,
    private orderMetaDataService: OrderMetaDataRepositoryService,
    protected ordersHub: OrdersHubService,
    private userGroupAccessRepo: UserGroupAccessRepositoryService,
    private consortiumService: ConsortiumService,
    private ordersService: OrderRepositoryService,
    private configurationState: ConfigurationState,
    private attestationTemplatesService: AttestationTemplateRepositoryService,
    private router: Router) { }

  ngOnInit(): void {
    this.orderTypeName = this.order.orderType.name;
    this.debtors = of(this.getDebtors());
    this.securedParties = of(this.getSecuredParties());
    this.dealers = of(this.getDealers());
    this.registeringAgents = of(this.getRegisteringAgents());
    this.assignors = of(this.order.parties!.filter(p => Utilities.isAssignor(p)));
    this.assignees = of(this.order.parties!.filter(p => Utilities.isAssignee(p)));
    this.businessParties = of(this.order.parties!);

    if (this.uiConfiguration.hasUltimateBeneficialOwnershipReportDetails) {
      this.attestor = this.order.parties!.find(p => Utilities.isAttestor(p));
      this.attestationTemplatesService.getAttestationTemplate(this.order.ultimateBeneficialOwnershipRequest?.templateID ?? '')
        .subscribe(template => {
          this.attestationTemplateName = template.name;
        });
    }

    this.shouldShowParsedJson();
    this.setTitles();

    this.getOrderMetaData();
    this.userGroupAccessRepo.getWriteableUserAccessesAsSelectOptions()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(userGroups => this._hasMultipleUserGroups = userGroups.length > 1);

    if ((this.order.orderTypeID === eOrderType.assetSearch ||
      this.order.orderTypeID === eOrderType.assetMultiSearch) &&
      this.order.assets?.at(0)?.serialNumber) {
      this.consortiumService.getSerialNumberTimelineSummary(this.order.assets.at(0)!.serialNumber)
        .subscribe(summary => this.handleSerialNumberTimelineSummary(summary));
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  getOrderMetaData() {
    this.orderMetaDataService.getOrderMetaData(null, this.order.userGroupID, this.order.userID, null)
      .subscribe(metaData => {
        this.requestingUser = `${metaData.requestingUserFullName} (${metaData.requestingUserName})`;
        this.requestingUserGroup = `${metaData.requestingUserGroupName}`;
      });
  }

  openOrderHistory() {
    this.dialog.open(OrderHistoryComponent, { data: this.order });
  }

  isPpsaSearchOrder(): boolean {
    return this.order.orderTypeID.toString() == "AssetSearch"
      || this.order.orderTypeID.toString() == "DebtorSearch"
      || this.order.orderTypeID.toString() == "CertifiedDebtorSearch"
      || this.order.orderTypeID.toString() == "RegistrationNumberSearch";
  }

  isMultiSearchOrder(): boolean {
    return this.order.orderTypeID === eOrderType.assetMultiSearch || this.order.orderTypeID === eOrderType.debtorMultiSearch;
  }

  isDischarge(): boolean {
    return this.order.orderTypeID.toString() == "Discharge"
      || this.order.orderTypeID.toString() == "BasicDischarge";
  }

  isRenewal(): boolean {
    return this.order.orderTypeID.toString() == "Renewal"
      || this.order.orderTypeID.toString() == "BasicRenewal";
  }

  isAmendment(): boolean {
    return this.order.orderTypeID.toString() == "Amendment";
  }

  showLifetime(): boolean {
    return (this.order.lien != null &&
      (!this.isDischarge() || !this.order.isLastOrder) &&
      this.order.orderTypeID !== eOrderType.assignment &&
      this.order.orderTypeID !== eOrderType.registrationNumberSearch &&
      this.order.orderStatusTypeID != "Cancelled");
  }

  shouldShowParsedJson() {
    this.configurationState.canDownloadJson()
      .then(showJSON => {
        this._showJSONParsedResults = showJSON;
      });
  }

  getDebtors(): Party[] {
    if (this.order.orderTypeID === eOrderType.landTitleSearch && this.order.landTitleSearchCriteria?.party) {
      return [this.order.landTitleSearchCriteria.party];
    } else if (this.order.orderTypeID === eOrderType.knowYourCustomer && this.order.knowYourCustomerCriteria?.party) {
      return [this.order.knowYourCustomerCriteria.party];
    } else {
      return this.order.parties?.filter((party) => Utilities.isDebtor(party)) ?? [];
    }
  }

  getSecuredParties(): Party[] {
    return this.order.parties?.filter((party) => Utilities.isSecuredParty(party)) ?? [];
  }

  getDealers(): Party[] {
    return this.order.parties?.filter((party) => Utilities.isDealer(party)) ?? [];
  }

  getRegisteringAgents(): Party[] {
    return this.order.parties?.filter((party) => Utilities.isRegisteringAgent(party)) ?? [];
  }

  getOrderTypeName(): string {
    return this.enumLabels.label('orderType', this.order.orderTypeID.toString(), this.order.orderType?.name);
  }

  orderHasSummary(): boolean {
    return !!(this.order.searchResult?.summaryDocument);
  }

  handleSerialNumberTimelineSummary(summary: TimelineSummary[]) {
    summary = summary.filter(s => s.hasRegistration);

    if (this.order.orderTypeID === eOrderType.assetSearch) {
      const index = summary.findIndex(s => s.jurisdiction === this.order.jurisdiction);

      if (index !== -1) {
        summary.splice(index, 1);
      }
    } else if (this.order.orderTypeID === eOrderType.assetMultiSearch) {
      this.order.searchParameters?.searchJurisdictions?.map(sj => sj.jurisdiction)?.forEach(j => {
        const index = summary.findIndex(s => s.jurisdiction === j);

        if (index !== -1) {
          summary.splice(index, 1);
        }
      });
    }

    this.summary = summary;
  }

  showConsortiumHints(): boolean {
    return this.order.orderStatusTypeID === eOrderStatusType.complete &&
      this.summary.length !== 0;
  }

  hasRedHint(): boolean {
    return this.summary.some(s => s.hasRegistration && s.isActivityOutsideRequestingCorporation);
  }

  getReadableIDVMethod(): string {
    const idvSources = this.order.identityVerificationDetails?.sources ?? [];
    let stringSources: string[] = [];
    if (idvSources.some(s => s.name == eIdentityVerificationSource.EfxAmlSingleSource)) {
      stringSources.push(Utilities.getIdRequirementsString(eIdentityVerificationSource.EfxAmlSingleSource));
    }
    if (idvSources.some(s => s.name == eIdentityVerificationSource.TuCreditBureau)) {
      stringSources.push(Utilities.getIdRequirementsString(eIdentityVerificationSource.TuCreditBureau));
    }
    if (idvSources.some(s => s.name == eIdentityVerificationSource.EfxDvs)) {
      stringSources.push(Utilities.getIdRequirementsString(eIdentityVerificationSource.EfxDvs));
    }
    if (idvSources.some(s => s.name == eIdentityVerificationSource.EntrustDvs)) {
      stringSources.push(Utilities.getIdRequirementsString(eIdentityVerificationSource.EntrustDvs));
    }
    return stringSources.join(', ');
  }

  showIdVStatus(): boolean {
    return (this.order.identityVerificationDetails?.sources.length ?? 0) > 0;
  }

  getReadableIDVStatus(): string {
    if (this.order.identityVerificationDetails?.sources?.some(s => s.status === eIdentityVerificationResult.Pending)) {
      return this.transloco.translate('orders.idvStatus.pending');
    } else if (this.order.identityVerificationDetails?.sources?.some(s => s.status === eIdentityVerificationResult.Review)) {
      return this.transloco.translate('orders.idvStatus.review');
    } else if (this.order.identityVerificationDetails?.sources?.some(s => s.status === eIdentityVerificationResult.Blocked)) {
      return this.transloco.translate('orders.idvStatus.blocked');
    } else if (this.order.identityVerificationDetails?.sources?.some(s => s.status === eIdentityVerificationResult.Success)) {
      return this.transloco.translate('orders.idvStatus.passed');
    } else {
      return this.transloco.translate('orders.idvStatus.failed');
    }
  }

  getHintJurisdictions() {
    return this.summary.map(s => s.jurisdiction).join(', ');
  }

  attestationRequestorDetailsVisible(): boolean {
    if (this.order.ultimateBeneficialOwnershipRequest?.displayedCorpName) {
      return this.order.ultimateBeneficialOwnershipRequest!.displayedCorpName != null;
    }
    return false;
  }

  onOrderSearch() {
    const jurisdictions = this.summary.map(s => s.jurisdiction);
    let newOrder: any = {
      referenceNumber: this.order.referenceNumber,
      country: this.order.country,
      assets: [
        {
          serialNumber: this.order.assets?.at(0)?.serialNumber
        }
      ],
      searchParameters: {}
    };

    if (jurisdictions.length === 1) {
      newOrder.orderTypeID = eOrderType.assetSearch;
      newOrder.jurisdiction = jurisdictions.at(0);
    } else {
      const searchJursidictions: any[] = [];

      jurisdictions.forEach(j => {
        searchJursidictions.push({ jurisdiction: j });
      });

      newOrder.orderTypeID = eOrderType.assetMultiSearch;
      newOrder.searchParameters.searchJurisdictions = searchJursidictions;
    }

    this.loading = true;
    this.ordersService.createNewOrder(newOrder, false).subscribe({
      next: order => {
        this.router.navigate(['orders', 'ppsa-search', order.resource.id]);
      },
      error: error => this.loading = false
    });
  }

  private setTitles() {
    if (this.order.orderTypeID === eOrderType.landTitleSearch) {
      this.debtorsTitle = 'orders.party';
    } else if (this.order.orderTypeID === eOrderType.knowYourCustomer) {
      this.debtorsTitle = 'orders.criteria';
    }
  }
}